/**
 * This file was automatically generated by ontology-generator.
 * DO NOT MODIFY IT BY HAND. Instead, modify the source JSON files in
 * tools/ontology-generator/src/nodespec/ and re-run the generator.
 */
import type { NodeLabel } from '../../types/schema'

/**
 * JSON-Schema-style description of a single node property, mirrored from
 * `tools/ontology-generator/src/nodespec/*.json`. `type` is the raw JSON
 * Schema primitive ('string' | 'number' | 'boolean' | 'object' | 'array').
 * `enum` lists the allowed string literal values when the property is an
 * enum. `items` describes the element schema for array-typed properties.
 */
export interface PropertySchema {
  type: 'string' | 'number' | 'boolean' | 'object' | 'array'
  enum?: readonly string[]
  items?: PropertySchema
}

/**
 * Per-label property schema: the keys allowed on the label plus the subset
 * that are required at creation time. Consumed by
 * `validateCypherWrite` to reject unknown properties, literal values of the
 * wrong type, enum violations, and missing required properties on CREATE /
 * MERGE.
 */
export interface NodePropertySchema {
  properties: Readonly<Record<string, PropertySchema>>
  required: readonly string[]
  oneOf?: readonly {
    properties: Readonly<Record<string, PropertySchema | false>>
    required: readonly string[]
  }[]
}

export const NODE_PROPERTY_SCHEMAS: Readonly<Record<NodeLabel, NodePropertySchema>> = {
  EquipmentProperty: {
    properties: {
      name: {
        type: 'string'
      },
      kind: {
        type: 'string',
        enum: ['observable', 'setpoint', 'unknown'] as const
      },
      unit: {
        type: 'string'
      },
      range_low: {
        type: 'number'
      },
      range_high: {
        type: 'number'
      },
      description: {
        type: 'string'
      }
    },
    required: ['name']
  },
  Observable: {
    properties: {
      name: {
        type: 'string'
      },
      unit: {
        type: 'string'
      },
      range_low: {
        type: 'number'
      },
      range_high: {
        type: 'number'
      },
      description: {
        type: 'string'
      }
    },
    required: ['name']
  },
  Setpoint: {
    properties: {
      name: {
        type: 'string'
      },
      unit: {
        type: 'string'
      },
      range_low: {
        type: 'number'
      },
      range_high: {
        type: 'number'
      },
      description: {
        type: 'string'
      }
    },
    required: ['name']
  },
  Part: {
    properties: {
      name: {
        type: 'string'
      },
      part_type: {
        type: 'string'
      },
      vendor: {
        type: 'string'
      },
      description: {
        type: 'string'
      },
      customer_pn: {
        type: 'string'
      },
      manufacturer_pn: {
        type: 'string'
      },
      manufacturer_name: {
        type: 'string'
      },
      is_spare: {
        type: 'boolean'
      },
      is_recommended_spare: {
        type: 'boolean'
      },
      is_critical_spare: {
        type: 'boolean'
      },
      bin_location: {
        type: 'string'
      },
      unit_of_measure: {
        type: 'string'
      }
    },
    required: ['name']
  },
  Customer: {
    properties: {
      name: {
        type: 'string'
      },
      customer_code: {
        type: 'string'
      },
      region: {
        type: 'string'
      }
    },
    required: ['name']
  },
  CustomerOrder: {
    properties: {
      name: {
        type: 'string'
      },
      customer_name: {
        type: 'string'
      },
      product_name: {
        type: 'string'
      },
      quantity: {
        type: 'number'
      },
      due_date: {
        type: 'string'
      }
    },
    required: ['name']
  },
  DemandForecast: {
    properties: {
      name: {
        type: 'string'
      },
      product_name: {
        type: 'string'
      },
      period: {
        type: 'string'
      },
      quantity: {
        type: 'number'
      }
    },
    required: ['name']
  },
  DemandHistory: {
    properties: {
      name: {
        type: 'string'
      },
      product_name: {
        type: 'string'
      },
      period: {
        type: 'string'
      },
      actual_quantity: {
        type: 'number'
      }
    },
    required: ['name']
  },
  Compute: {
    properties: {
      name: {
        type: 'string'
      },
      compute_type: {
        type: 'string',
        enum: ['ignition', 'aveva', 'opc', 'db_server', 'device', 'other'] as const
      },
      host: {
        type: 'string'
      },
      url: {
        type: 'string'
      },
      version: {
        type: 'string'
      },
      vendor: {
        type: 'string'
      },
      opc_endpoint: {
        type: 'string'
      },
      opc_root_node_id: {
        type: 'string'
      },
      opc_security_mode: {
        type: 'string'
      },
      opc_bind_port: {
        type: 'number'
      },
      opc_bind_addresses: {
        type: 'array',
        items: {
          type: 'string'
        }
      },
      opc_endpoint_addresses: {
        type: 'array',
        items: {
          type: 'string'
        }
      },
      opc_security_policies: {
        type: 'array',
        items: {
          type: 'string'
        }
      },
      opc_anonymous_access: {
        type: 'boolean'
      },
      opc_auth_profile: {
        type: 'string'
      },
      source_file: {
        type: 'string'
      },
      web_dev_project: {
        type: 'string'
      },
      kind: {
        type: 'string'
      }
    },
    required: ['name']
  },
  TagProvider: {
    properties: {
      name: {
        type: 'string'
      },
      protocol: {
        type: 'string',
        enum: [
          'ignition',
          'aveva',
          'opc-ua',
          'opc-da',
          'mqtt',
          'mtconnect',
          'pi',
          'remote'
        ] as const
      },
      url: {
        type: 'string'
      },
      enabled: {
        type: 'boolean'
      },
      remote_compute_url: {
        type: 'string'
      },
      remote_provider_name: {
        type: 'string'
      }
    },
    required: []
  },
  Tag: {
    properties: {
      name: {
        type: 'string'
      },
      tag_path: {
        type: 'string'
      },
      tag_system: {
        type: 'string',
        enum: ['ignition', 'aveva', 'opc', 'db', 'mtconnect', 'pi', 'canary'] as const
      },
      ignition_tag_type: {
        type: 'string',
        enum: [
          'atomic',
          'query',
          'expression',
          'opc',
          'derived',
          'memory',
          'reference',
          'udt_member'
        ] as const
      },
      mtc_category: {
        type: 'string'
      },
      aveva_tag_type: {
        type: 'string'
      },
      data_type: {
        type: 'string'
      },
      folder: {
        type: 'string'
      },
      opc_item_path: {
        type: 'string'
      },
      opc_server: {
        type: 'string'
      },
      plc_tag_path: {
        type: 'string'
      },
      opc_node_id: {
        type: 'string'
      },
      opc_browse_path: {
        type: 'array',
        items: {
          type: 'string'
        }
      },
      opc_live_surface_id: {
        type: 'string'
      },
      mtc_type: {
        type: 'string'
      },
      mtc_sub_type: {
        type: 'string'
      },
      mtc_uuid: {
        type: 'string'
      },
      mtc_data_item_name: {
        type: 'string'
      },
      pi_web_id: {
        type: 'string'
      },
      pi_point_name: {
        type: 'string'
      },
      pi_dataserver_web_id: {
        type: 'string'
      },
      pi_dataserver_name: {
        type: 'string'
      },
      mtc_native_units: {
        type: 'string'
      },
      expression: {
        type: 'string'
      },
      datasource: {
        type: 'string'
      },
      query: {
        type: 'string'
      },
      initial_value: {
        type: 'string'
      },
      aveva_tag_name: {
        type: 'string'
      },
      aveva_attribute_name: {
        type: 'string'
      },
      aveva_defining_template: {
        type: 'string'
      },
      documentation: {
        type: 'string'
      },
      eng_unit: {
        type: 'string'
      },
      category: {
        type: 'string'
      },
      equipment_hint: {
        type: 'string'
      },
      eng_low: {
        type: 'number'
      },
      eng_high: {
        type: 'number'
      },
      read_only: {
        type: 'boolean'
      },
      history_enabled: {
        type: 'boolean'
      },
      llm_class: {
        type: 'string',
        enum: ['numeric', 'boolean_enum', 'other'] as const
      },
      llm_role: {
        type: 'string',
        enum: [
          'accumulator',
          'sensor',
          'setpoint',
          'control_output',
          'identifier_code',
          'other'
        ] as const
      },
      llm_is_percentage: {
        type: 'boolean'
      },
      llm_expected_range_low: {
        type: 'number'
      },
      llm_expected_range_high: {
        type: 'number'
      },
      llm_good_states: {
        type: 'array',
        items: {
          type: 'string'
        }
      },
      llm_bad_states: {
        type: 'array',
        items: {
          type: 'string'
        }
      },
      llm_warning_states: {
        type: 'array',
        items: {
          type: 'string'
        }
      },
      llm_confidence: {
        type: 'string',
        enum: ['low', 'medium', 'high'] as const
      },
      llm_reasoning: {
        type: 'string'
      },
      llm_model: {
        type: 'string'
      },
      llm_classified_at: {
        type: 'string'
      }
    },
    required: ['name', 'tag_path', 'tag_system']
  },
  Alarm: {
    properties: {
      name: {
        type: 'string'
      },
      tag_path: {
        type: 'string'
      },
      priority: {
        type: 'string',
        enum: ['Diagnostic', 'Low', 'Medium', 'High', 'Critical'] as const
      },
      mode: {
        type: 'string'
      },
      enabled: {
        type: 'boolean'
      },
      enabled_binding: {
        type: 'string'
      },
      setpoint_a: {
        type: 'number'
      },
      setpoint_a_binding: {
        type: 'string'
      },
      setpoint_b: {
        type: 'number'
      },
      setpoint_b_binding: {
        type: 'string'
      },
      inclusive_a: {
        type: 'boolean'
      },
      inclusive_b: {
        type: 'boolean'
      },
      deadband: {
        type: 'number'
      },
      deadband_mode: {
        type: 'string'
      },
      time_on_delay_seconds: {
        type: 'number'
      },
      time_off_delay_seconds: {
        type: 'number'
      },
      ack_mode: {
        type: 'string'
      },
      ack_notes_required: {
        type: 'boolean'
      },
      label: {
        type: 'string'
      },
      label_binding: {
        type: 'string'
      },
      display_path: {
        type: 'string'
      },
      display_path_binding: {
        type: 'string'
      },
      notes: {
        type: 'string'
      },
      any_change: {
        type: 'boolean'
      },
      shelving_allowed: {
        type: 'boolean'
      },
      timestamp_source: {
        type: 'string'
      },
      active_pipeline: {
        type: 'string'
      },
      ack_pipeline: {
        type: 'string'
      },
      clear_pipeline: {
        type: 'string'
      },
      custom_email_subject: {
        type: 'string'
      },
      custom_email_message: {
        type: 'string'
      },
      custom_sms_message: {
        type: 'string'
      },
      custom_voip_message: {
        type: 'string'
      }
    },
    required: ['name']
  },
  Database: {
    properties: {
      name: {
        type: 'string'
      },
      database_type: {
        type: 'string'
      },
      url: {
        type: 'string'
      },
      enabled: {
        type: 'boolean'
      },
      validation_query: {
        type: 'string'
      }
    },
    required: ['name']
  },
  Folder: {
    properties: {
      name: {
        type: 'string'
      },
      path: {
        type: 'string'
      },
      provider_name: {
        type: 'string'
      },
      depth: {
        type: 'number'
      }
    },
    required: ['name', 'path']
  },
  Table: {
    properties: {
      name: {
        type: 'string'
      },
      schema: {
        type: 'string'
      },
      columns: {
        type: 'array',
        items: {
          type: 'string'
        }
      },
      primary_key: {
        type: 'string'
      }
    },
    required: ['name']
  },
  NamedQuery: {
    properties: {
      name: {
        type: 'string'
      },
      path: {
        type: 'string'
      },
      project: {
        type: 'string'
      },
      query_text: {
        type: 'string'
      },
      database: {
        type: 'string'
      },
      query_type: {
        type: 'string'
      },
      syntax: {
        type: 'string'
      },
      parameters: {
        type: 'array',
        items: {
          type: 'object'
        }
      },
      columns: {
        type: 'array',
        items: {
          type: 'string'
        }
      },
      folder_path: {
        type: 'string'
      }
    },
    required: ['name']
  },
  QueryTag: {
    properties: {
      name: {
        type: 'string'
      },
      datasource: {
        type: 'string'
      },
      query: {
        type: 'string'
      },
      polling_rate: {
        type: 'number'
      }
    },
    required: ['name']
  },
  DcsController: {
    properties: {
      name: {
        type: 'string'
      },
      display_name: {
        type: 'string'
      },
      vendor: {
        type: 'string'
      },
      model: {
        type: 'string'
      },
      ip_address: {
        type: 'string'
      },
      comm_path: {
        type: 'string'
      },
      kind: {
        type: 'string'
      },
      redundancy_role: {
        type: 'string',
        enum: ['primary', 'secondary'] as const
      },
      source_guid: {
        type: 'string'
      }
    },
    required: ['name']
  },
  Cee: {
    properties: {
      name: {
        type: 'string'
      },
      display_name: {
        type: 'string'
      },
      ip_address: {
        type: 'string'
      },
      comm_path: {
        type: 'string'
      },
      kind: {
        type: 'string'
      },
      source_guid: {
        type: 'string'
      }
    },
    required: ['name']
  },
  ControlModule: {
    properties: {
      name: {
        type: 'string'
      },
      display_name: {
        type: 'string'
      },
      module_type: {
        type: 'string',
        enum: ['cm', 'scm'] as const
      },
      description: {
        type: 'string'
      },
      ee_name: {
        type: 'string'
      },
      period: {
        type: 'string'
      },
      kind: {
        type: 'string'
      },
      source_guid: {
        type: 'string'
      }
    },
    required: ['name', 'module_type']
  },
  FunctionBlock: {
    properties: {
      name: {
        type: 'string'
      },
      display_name: {
        type: 'string'
      },
      block_type: {
        type: 'string'
      },
      template_name: {
        type: 'string'
      },
      block_role: {
        type: 'string',
        enum: ['logic', 'step', 'transition', 'handler', 'comms', 'device'] as const
      },
      kind: {
        type: 'string'
      },
      source_guid: {
        type: 'string'
      }
    },
    required: ['name', 'block_type']
  },
  BlockType: {
    properties: {
      name: {
        type: 'string'
      },
      class_name: {
        type: 'string'
      },
      comp_type: {
        type: 'string'
      },
      create_type: {
        type: 'string',
        enum: ['system', 'user'] as const
      }
    },
    required: ['name']
  },
  BlockParamGroup: {
    properties: {
      name: {
        type: 'string'
      },
      group_number: {
        type: 'number'
      },
      parent_group: {
        type: 'number'
      },
      param_names: {
        type: 'array',
        items: {
          type: 'string'
        }
      }
    },
    required: ['name']
  },
  BlockLibrary: {
    properties: {
      name: {
        type: 'string'
      },
      release: {
        type: 'string'
      }
    },
    required: ['name']
  },
  IoModule: {
    properties: {
      name: {
        type: 'string'
      },
      card_type: {
        type: 'string'
      },
      iolink_name: {
        type: 'string'
      },
      slot: {
        type: 'string'
      },
      source_guid: {
        type: 'string'
      }
    },
    required: ['name']
  },
  IoChannel: {
    properties: {
      name: {
        type: 'string'
      },
      channel_type: {
        type: 'string',
        enum: ['analog_in', 'analog_out', 'discrete_in', 'discrete_out', 'spare'] as const
      },
      channel_num: {
        type: 'string'
      },
      slot_num: {
        type: 'string'
      },
      spare: {
        type: 'boolean'
      },
      source_guid: {
        type: 'string'
      }
    },
    required: ['name', 'channel_type']
  },
  ControlNamespace: {
    properties: {
      name: {
        type: 'string'
      },
      vendor: {
        type: 'string'
      }
    },
    required: ['name']
  },
  ControlTag: {
    properties: {
      name: {
        type: 'string'
      },
      param: {
        type: 'string'
      },
      origin_block: {
        type: 'string'
      }
    },
    required: ['name', 'param']
  },
  ControlAlarm: {
    properties: {
      name: {
        type: 'string'
      },
      alarm_type: {
        type: 'string'
      },
      priority: {
        type: 'string'
      }
    },
    required: ['name']
  },
  ControlSetpoint: {
    properties: {
      name: {
        type: 'string'
      },
      value: {
        type: 'string'
      }
    },
    required: ['name']
  },
  HumanLabor: {
    properties: {
      name: {
        type: 'string'
      },
      description: {
        type: 'string'
      },
      operation_kind: {
        type: 'string',
        enum: [
          'load',
          'unload',
          'transfer',
          'manual_process',
          'inspection',
          'pack',
          'other'
        ] as const
      },
      ad_hoc: {
        type: 'boolean'
      }
    },
    required: ['name']
  },
  CrewRequirement: {
    properties: {
      name: {
        type: 'string'
      },
      headcount: {
        type: 'number'
      },
      description: {
        type: 'string'
      }
    },
    required: ['name', 'headcount']
  },
  Project: {
    properties: {
      name: {
        type: 'string'
      },
      title: {
        type: 'string'
      },
      description: {
        type: 'string'
      },
      enabled: {
        type: 'boolean'
      },
      inheritable: {
        type: 'boolean'
      },
      parent: {
        type: 'string'
      },
      runtime_kind: {
        type: 'string',
        enum: [
          'ignition_perspective',
          'ignition_vision',
          'factorytalk_view_me',
          'factorytalk_view_se',
          'wonderware_intouch',
          'siemens_wincc',
          'aveva_intouch_omi',
          'other'
        ] as const
      }
    },
    required: ['name']
  },
  View: {
    properties: {
      name: {
        type: 'string'
      },
      path: {
        type: 'string'
      },
      view_type: {
        type: 'string',
        enum: ['perspective', 'vision'] as const
      },
      title: {
        type: 'string'
      },
      project: {
        type: 'string'
      },
      runtime_kind: {
        type: 'string',
        enum: [
          'ignition_perspective',
          'ignition_vision',
          'factorytalk_view_me',
          'factorytalk_view_se',
          'wonderware_intouch',
          'siemens_wincc',
          'aveva_intouch_omi',
          'other'
        ] as const
      }
    },
    required: ['name']
  },
  ViewComponent: {
    properties: {
      name: {
        type: 'string'
      },
      component_type: {
        type: 'string'
      },
      view_name: {
        type: 'string'
      },
      bindings: {
        type: 'array',
        items: {
          type: 'string'
        }
      },
      props: {
        type: 'object'
      }
    },
    required: ['name']
  },
  UDTDefinition: {
    properties: {
      name: {
        type: 'string'
      },
      type_id: {
        type: 'string'
      },
      parent_type: {
        type: 'string'
      },
      folder: {
        type: 'string'
      }
    },
    required: ['name']
  },
  UDTMember: {
    properties: {
      name: {
        type: 'string'
      },
      member_type: {
        type: 'string',
        enum: [
          'opc',
          'memory',
          'expression',
          'derived',
          'reference',
          'query',
          'atomic',
          'udt_instance'
        ] as const
      },
      data_type: {
        type: 'string'
      },
      expression: {
        type: 'string'
      },
      source_tag_path: {
        type: 'string'
      },
      read_expression: {
        type: 'string'
      },
      write_expression: {
        type: 'string'
      },
      opc_item_path: {
        type: 'string'
      },
      opc_server: {
        type: 'string'
      },
      query: {
        type: 'string'
      },
      datasource: {
        type: 'string'
      },
      query_type: {
        type: 'string'
      },
      value: {
        type: 'string'
      },
      default_value: {
        type: 'string'
      },
      format_string: {
        type: 'string'
      },
      is_override: {
        type: 'boolean'
      },
      ancestor: {
        type: 'string'
      },
      udt_type_id: {
        type: 'string'
      }
    },
    required: ['name']
  },
  UDTParameter: {
    properties: {
      name: {
        type: 'string'
      },
      data_type: {
        type: 'string'
      },
      value: {
        type: 'string'
      }
    },
    required: ['name']
  },
  UDTInstance: {
    properties: {
      name: {
        type: 'string'
      },
      udt_name: {
        type: 'string'
      },
      folder: {
        type: 'string'
      }
    },
    required: ['name']
  },
  Script: {
    properties: {
      name: {
        type: 'string'
      },
      path: {
        type: 'string'
      },
      project: {
        type: 'string'
      },
      scope: {
        type: 'string'
      },
      script_text: {
        type: 'string'
      }
    },
    required: ['name']
  },
  GatewayEvent: {
    properties: {
      name: {
        type: 'string'
      },
      project_name: {
        type: 'string'
      },
      startup_script: {
        type: 'string'
      },
      shutdown_script: {
        type: 'string'
      },
      update_script: {
        type: 'string'
      }
    },
    required: ['name']
  },
  GatewayEventHandler: {
    properties: {
      name: {
        type: 'string'
      },
      handler_type: {
        type: 'string',
        enum: ['timer', 'tag_change', 'message', 'scheduled'] as const
      },
      script_text: {
        type: 'string'
      },
      enabled: {
        type: 'boolean'
      },
      delay: {
        type: 'number'
      },
      fixed_delay: {
        type: 'boolean'
      },
      shared_thread: {
        type: 'boolean'
      },
      cron_expression: {
        type: 'string'
      },
      thread_type: {
        type: 'string'
      },
      tag_paths: {
        type: 'array',
        items: {
          type: 'string'
        }
      },
      change_triggers: {
        type: 'array',
        items: {
          type: 'string'
        }
      }
    },
    required: ['name']
  },
  AuditProfile: {
    properties: {
      name: {
        type: 'string'
      }
    },
    required: ['name']
  },
  ScheduleProfile: {
    properties: {
      name: {
        type: 'string'
      },
      description: {
        type: 'string'
      },
      schedule_type: {
        type: 'string',
        enum: ['basic schedule', 'composite schedule'] as const
      },
      observe_holidays: {
        type: 'boolean'
      },
      repeat_mode: {
        type: 'string'
      },
      config_json: {
        type: 'string'
      }
    },
    required: ['name']
  },
  Holiday: {
    properties: {
      name: {
        type: 'string'
      },
      date: {
        type: 'string'
      },
      repeat_annually: {
        type: 'boolean'
      }
    },
    required: ['name']
  },
  Product: {
    properties: {
      name: {
        type: 'string'
      },
      product_code: {
        type: 'string'
      },
      category: {
        type: 'string'
      }
    },
    required: ['name']
  },
  Material: {
    properties: {
      name: {
        type: 'string'
      },
      material_type: {
        type: 'string',
        enum: ['ROH', 'HALB', 'FERT'] as const
      },
      unit_of_measure: {
        type: 'string'
      }
    },
    required: ['name']
  },
  Recipe: {
    properties: {
      name: {
        type: 'string'
      },
      product_name: {
        type: 'string'
      },
      version: {
        type: 'string'
      }
    },
    required: ['name']
  },
  Phase: {
    properties: {
      name: {
        type: 'string'
      },
      recipe_name: {
        type: 'string'
      },
      sequence_number: {
        type: 'number'
      }
    },
    required: ['name']
  },
  WorkOrder: {
    properties: {
      name: {
        type: 'string'
      },
      product_name: {
        type: 'string'
      },
      status: {
        type: 'string'
      },
      quantity: {
        type: 'number'
      },
      scheduled_start: {
        type: 'string'
      },
      scheduled_end: {
        type: 'string'
      }
    },
    required: ['name']
  },
  ProductionGoal: {
    properties: {
      name: {
        type: 'string'
      },
      goal_id: {
        type: 'string'
      },
      scenario_id: {
        type: 'string'
      },
      target_kind: {
        type: 'string',
        enum: ['static', 'tag'] as const
      },
      target_value: {
        type: 'number'
      },
      target_unit: {
        type: 'string'
      },
      window: {
        type: 'string',
        enum: ['run', 'shift'] as const
      },
      source_kind: {
        type: 'string'
      }
    },
    required: ['name', 'goal_id', 'target_kind', 'window']
  },
  Batch: {
    properties: {
      name: {
        type: 'string'
      },
      work_order_name: {
        type: 'string'
      },
      status: {
        type: 'string'
      },
      actual_start: {
        type: 'string'
      },
      actual_end: {
        type: 'string'
      }
    },
    required: ['name']
  },
  Operation: {
    properties: {
      name: {
        type: 'string'
      },
      production_order_name: {
        type: 'string'
      },
      sequence_number: {
        type: 'number'
      },
      status: {
        type: 'string'
      }
    },
    required: ['name']
  },
  ProcessDeviation: {
    properties: {
      name: {
        type: 'string'
      },
      batch_name: {
        type: 'string'
      },
      equipment_name: {
        type: 'string'
      },
      deviation_type: {
        type: 'string'
      },
      severity: {
        type: 'string'
      },
      detected_at: {
        type: 'string'
      }
    },
    required: ['name']
  },
  FunctionalLocation: {
    properties: {
      name: {
        type: 'string'
      },
      location_type: {
        type: 'string'
      },
      parent_location: {
        type: 'string'
      }
    },
    required: ['name']
  },
  Vendor: {
    properties: {
      name: {
        type: 'string'
      },
      vendor_code: {
        type: 'string'
      },
      contact: {
        type: 'string'
      },
      vendor_kind: {
        type: 'string',
        enum: [
          'oem',
          'sub_manufacturer',
          'distributor',
          'general_supplier',
          'internal_category'
        ] as const
      }
    },
    required: ['name']
  },
  QualityResult: {
    properties: {
      name: {
        type: 'string'
      },
      batch_name: {
        type: 'string'
      },
      parameter: {
        type: 'string'
      },
      value: {
        type: 'number'
      },
      unit: {
        type: 'string'
      },
      pass: {
        type: 'boolean'
      }
    },
    required: ['name']
  },
  Asset: {
    properties: {
      name: {
        type: 'string'
      },
      display_name: {
        type: 'string'
      },
      full_item_name: {
        type: 'string'
      },
      description: {
        type: 'string'
      },
      source_guid: {
        type: 'string'
      },
      kind: {
        type: 'string'
      }
    },
    required: ['name']
  },
  NetworkSegment: {
    properties: {
      name: {
        type: 'string'
      },
      display_name: {
        type: 'string'
      },
      segment_kind: {
        type: 'string',
        enum: ['segment', 'fte_community'] as const
      },
      description: {
        type: 'string'
      },
      source_guid: {
        type: 'string'
      },
      kind: {
        type: 'string'
      }
    },
    required: ['name']
  },
  NetworkDevice: {
    properties: {
      name: {
        type: 'string'
      },
      display_name: {
        type: 'string'
      },
      device_kind: {
        type: 'string',
        enum: ['switch', 'fte_device', 'net_equip', 'other'] as const
      },
      ip_address: {
        type: 'string'
      },
      description: {
        type: 'string'
      },
      source_guid: {
        type: 'string'
      },
      kind: {
        type: 'string'
      }
    },
    required: ['name']
  },
  EmdbProject: {
    properties: {
      name: {
        type: 'string'
      },
      description: {
        type: 'string'
      },
      project_type: {
        type: 'number'
      }
    },
    required: ['name']
  },
  Organization: {
    properties: {
      name: {
        type: 'string'
      },
      code: {
        type: 'string'
      },
      description: {
        type: 'string'
      }
    },
    required: ['name']
  },
  Site: {
    properties: {
      name: {
        type: 'string'
      },
      description: {
        type: 'string'
      }
    },
    required: ['name']
  },
  Controller: {
    properties: {
      name: {
        type: 'string'
      },
      type: {
        type: 'string'
      },
      vendor: {
        type: 'string'
      },
      firmware_version: {
        type: 'string'
      },
      project_name: {
        type: 'string'
      },
      plc_baseline_hash: {
        type: 'string'
      },
      plc_baseline_doc_key: {
        type: 'string'
      },
      plc_baseline_schema_version: {
        type: 'number'
      }
    },
    required: ['name']
  },
  Program: {
    properties: {
      name: {
        type: 'string'
      },
      controller_name: {
        type: 'string'
      },
      type: {
        type: 'string'
      },
      folder: {
        type: 'string'
      },
      title: {
        type: 'string'
      },
      last_change: {
        type: 'string'
      },
      statements: {
        type: 'array',
        items: {
          type: 'object'
        }
      }
    },
    required: ['name']
  },
  Routine: {
    properties: {
      name: {
        type: 'string'
      },
      program_name: {
        type: 'string'
      },
      aoi_name: {
        type: 'string'
      },
      type: {
        type: 'string',
        enum: ['RLL', 'LAD', 'ST', 'SCL', 'FBD', 'SFC', 'SC', 'L5X', 'Unknown'] as const
      },
      source_code: {
        type: 'string'
      },
      rungs: {
        type: 'array',
        items: {
          type: 'object'
        }
      }
    },
    required: ['name']
  },
  AOI: {
    properties: {
      name: {
        type: 'string'
      },
      revision: {
        type: 'string'
      },
      description: {
        type: 'string'
      },
      parameters: {
        type: 'array',
        items: {
          type: 'object'
        }
      },
      source_code: {
        type: 'string'
      }
    },
    required: ['name']
  },
  PLCDataType: {
    properties: {
      name: {
        type: 'string'
      },
      description: {
        type: 'string'
      }
    },
    required: ['name']
  },
  PLCDataTypeMember: {
    properties: {
      name: {
        type: 'string'
      },
      data_type: {
        type: 'string'
      },
      description: {
        type: 'string'
      }
    },
    required: ['name']
  },
  PLCTag: {
    properties: {
      name: {
        type: 'string'
      },
      data_type: {
        type: 'string'
      },
      device_class: {
        type: 'string'
      },
      direction: {
        type: 'string',
        enum: ['INPUT', 'OUTPUT', 'IN_OUT'] as const
      },
      description: {
        type: 'string'
      },
      default_value: {
        type: 'string'
      },
      is_array: {
        type: 'boolean'
      },
      array_bounds: {
        type: 'array',
        items: {
          type: 'number'
        }
      },
      aoi_name: {
        type: 'string'
      }
    },
    required: ['name']
  },
  ControlPattern: {
    properties: {
      name: {
        type: 'string'
      },
      pattern_type: {
        type: 'string'
      },
      description: {
        type: 'string'
      }
    },
    required: ['name']
  },
  DataFlow: {
    properties: {
      name: {
        type: 'string'
      },
      source_name: {
        type: 'string'
      },
      target_name: {
        type: 'string'
      },
      flow_type: {
        type: 'string'
      },
      description: {
        type: 'string'
      }
    },
    required: ['name']
  },
  Procedure: {
    properties: {
      title: {
        type: 'string'
      },
      procedure_kind: {
        type: 'string',
        enum: [
          'inspection',
          'changeover',
          'cleaning',
          'loto',
          'sop',
          'calibration',
          'other'
        ] as const
      },
      frequency: {
        type: 'string'
      },
      duration_minutes: {
        type: 'number'
      },
      authored_by: {
        type: 'string'
      },
      source_doc_id: {
        type: 'string'
      },
      source_doc_page: {
        type: 'number'
      },
      steps: {
        type: 'array',
        items: {
          type: 'object'
        }
      },
      ppe_required: {
        type: 'array',
        items: {
          type: 'string'
        }
      },
      loto_required: {
        type: 'boolean'
      },
      description: {
        type: 'string'
      }
    },
    required: ['title', 'procedure_kind']
  },
  Process: {
    properties: {
      name: {
        type: 'string'
      },
      description: {
        type: 'string'
      },
      inputs: {
        type: 'array',
        items: {
          type: 'string'
        }
      },
      outputs: {
        type: 'array',
        items: {
          type: 'string'
        }
      },
      process_kind: {
        type: 'string'
      }
    },
    required: ['name']
  },
  Line: {
    properties: {
      name: {
        type: 'string'
      },
      line_code: {
        type: 'string'
      },
      description: {
        type: 'string'
      }
    },
    required: ['name']
  },
  LineOperation: {
    properties: {
      name: {
        type: 'string'
      },
      sequence_number: {
        type: 'number'
      },
      description: {
        type: 'string'
      },
      operation_kind: {
        type: 'string'
      },
      inputs: {
        type: 'array',
        items: {
          type: 'string'
        }
      },
      outputs: {
        type: 'array',
        items: {
          type: 'string'
        }
      },
      ad_hoc: {
        type: 'boolean'
      }
    },
    required: ['name']
  },
  Equipment: {
    properties: {
      name: {
        type: 'string'
      },
      equipment_type: {
        type: 'string'
      },
      hierarchical_name: {
        type: 'string'
      },
      area_name: {
        type: 'string'
      },
      hosted_by: {
        type: 'string'
      },
      aveva_root_family: {
        type: 'string'
      },
      aveva_direct_template: {
        type: 'string'
      },
      aveva_tag_name: {
        type: 'string'
      },
      aveva_is_template: {
        type: 'boolean'
      },
      opc_node_id: {
        type: 'string'
      },
      opc_live_surface_id: {
        type: 'string'
      },
      opc_browse_path: {
        type: 'array',
        items: {
          type: 'string'
        }
      },
      tag_number: {
        type: 'string'
      },
      vendor: {
        type: 'string'
      },
      description: {
        type: 'string'
      },
      model: {
        type: 'string'
      },
      datasheet_no: {
        type: 'string'
      },
      pid_number: {
        type: 'string'
      },
      io_type: {
        type: 'string'
      },
      safety_rated: {
        type: 'boolean'
      },
      mct: {
        type: 'boolean'
      },
      trace_heating: {
        type: 'string'
      },
      winterisation: {
        type: 'string'
      },
      leak_class: {
        type: 'string'
      },
      size: {
        type: 'string'
      },
      capacity: {
        type: 'number'
      },
      power: {
        type: 'number'
      },
      material: {
        type: 'string'
      },
      unit: {
        type: 'string'
      },
      range_low: {
        type: 'number'
      },
      range_high: {
        type: 'number'
      }
    },
    required: ['name']
  },
  PssTag: {
    properties: {
      name: {
        type: 'string'
      },
      data_owner_id: {
        type: 'string'
      },
      data_owner_name: {
        type: 'string'
      },
      description: {
        type: 'string'
      },
      unit: {
        type: 'string'
      },
      connection_id: {
        type: 'string'
      },
      mirror_id: {
        type: 'string'
      },
      source_row_key: {
        type: 'string'
      },
      active: {
        type: 'boolean'
      }
    },
    required: ['name', 'data_owner_id', 'connection_id']
  },
  PssEventJournal: {
    properties: {
      name: {
        type: 'string'
      },
      journal_kind: {
        type: 'string'
      },
      data_owner_id: {
        type: 'string'
      },
      data_owner_name: {
        type: 'string'
      },
      table_name: {
        type: 'string'
      },
      connection_id: {
        type: 'string'
      },
      mirror_id: {
        type: 'string'
      }
    },
    required: ['name', 'journal_kind', 'data_owner_id', 'table_name', 'connection_id']
  },
  RobotController: {
    properties: {
      name: {
        type: 'string'
      },
      display_name: {
        type: 'string'
      },
      vendor: {
        type: 'string',
        enum: [
          'Yaskawa',
          'FANUC',
          'ABB',
          'KUKA',
          'Kawasaki',
          'Mitsubishi',
          'Nachi',
          'Denso',
          'Staubli',
          'UniversalRobots',
          'Comau',
          'Yamaha',
          'Other'
        ] as const
      },
      model: {
        type: 'string'
      },
      controller_series: {
        type: 'string'
      },
      application: {
        type: 'string'
      },
      software_version: {
        type: 'string'
      },
      ip_address: {
        type: 'string'
      },
      metadata_method: {
        type: 'string'
      },
      needs_review: {
        type: 'boolean'
      }
    },
    required: ['name']
  },
  RobotArm: {
    properties: {
      name: {
        type: 'string'
      },
      group: {
        type: 'string'
      },
      model: {
        type: 'string'
      },
      axis_count: {
        type: 'number'
      },
      payload_kg: {
        type: 'number'
      },
      assignment_method: {
        type: 'string'
      },
      needs_review: {
        type: 'boolean'
      }
    },
    required: ['name']
  },
  RobotJob: {
    properties: {
      name: {
        type: 'string'
      },
      job_type: {
        type: 'string'
      },
      language: {
        type: 'string',
        enum: ['JBI', 'TP', 'KAREL', 'BASIC', 'BASIC2'] as const
      },
      execution_model: {
        type: 'string',
        enum: ['subroutine', 'task', 'background'] as const
      },
      source_file: {
        type: 'string'
      },
      vendor: {
        type: 'string',
        enum: [
          'Yaskawa',
          'FANUC',
          'ABB',
          'KUKA',
          'Kawasaki',
          'Mitsubishi',
          'Nachi',
          'Denso',
          'Staubli',
          'UniversalRobots',
          'Comau',
          'Yamaha',
          'Other'
        ] as const
      },
      group: {
        type: 'string'
      },
      comment: {
        type: 'string'
      },
      is_master: {
        type: 'boolean'
      },
      instruction_count: {
        type: 'number'
      },
      position_count: {
        type: 'number'
      },
      source_code: {
        type: 'string'
      },
      master_job_method: {
        type: 'string'
      },
      assignment_method: {
        type: 'string'
      },
      needs_review: {
        type: 'boolean'
      }
    },
    required: ['name']
  },
  RobotIOSignal: {
    properties: {
      name: {
        type: 'string'
      },
      address: {
        type: 'string'
      },
      direction: {
        type: 'string',
        enum: ['IN', 'OUT'] as const
      },
      signal_number: {
        type: 'number'
      },
      source_format: {
        type: 'string'
      },
      source_file: {
        type: 'string'
      },
      comment: {
        type: 'string'
      },
      signal_family: {
        type: 'string'
      },
      signal_index: {
        type: 'number'
      },
      rack: {
        type: 'string'
      },
      slot: {
        type: 'string'
      },
      start_point: {
        type: 'number'
      },
      width: {
        type: 'number'
      },
      port: {
        type: 'number'
      },
      bit: {
        type: 'number'
      },
      word_span: {
        type: 'string'
      },
      assembly_byte: {
        type: 'number'
      },
      assembly_bit: {
        type: 'number'
      }
    },
    required: ['name']
  },
  RobotCondition: {
    properties: {
      name: {
        type: 'string'
      },
      scheme: {
        type: 'string'
      },
      index: {
        type: 'number'
      },
      condition_type: {
        type: 'string'
      },
      process_type: {
        type: 'string',
        enum: ['arc_weld', 'spot_weld', 'seal', 'other'] as const
      },
      source_format: {
        type: 'string'
      },
      source_file: {
        type: 'string'
      },
      comment: {
        type: 'string'
      }
    },
    required: ['name']
  },
  VisionProcess: {
    properties: {
      name: {
        type: 'string'
      },
      vendor: {
        type: 'string',
        enum: [
          'Yaskawa',
          'FANUC',
          'ABB',
          'KUKA',
          'Kawasaki',
          'Mitsubishi',
          'Nachi',
          'Denso',
          'Staubli',
          'UniversalRobots',
          'Comau',
          'Yamaha',
          'Other'
        ] as const
      },
      vision_system: {
        type: 'string'
      },
      type_label: {
        type: 'string'
      },
      tools: {
        type: 'array',
        items: {
          type: 'string'
        }
      },
      applications: {
        type: 'array',
        items: {
          type: 'string',
          enum: ['guidance', 'inspection', 'reading', 'count'] as const
        }
      },
      result_kinds: {
        type: 'array',
        items: {
          type: 'string'
        }
      },
      operations: {
        type: 'array',
        items: {
          type: 'string'
        }
      },
      vision_registers: {
        type: 'array',
        items: {
          type: 'number'
        }
      },
      camera_views: {
        type: 'array',
        items: {
          type: 'number'
        }
      },
      offset_applied: {
        type: 'boolean'
      },
      remote_controller: {
        type: 'string'
      },
      has_vision_data_file: {
        type: 'boolean'
      },
      vision_data_size_bytes: {
        type: 'number'
      },
      source_file: {
        type: 'string'
      },
      source_format: {
        type: 'string'
      },
      comment: {
        type: 'string'
      },
      needs_review: {
        type: 'boolean'
      }
    },
    required: ['name']
  },
  VisionData: {
    properties: {
      name: {
        type: 'string'
      },
      vision_data_type: {
        type: 'string',
        enum: [
          'camera_setup',
          'camera_calibration',
          'vision_override',
          'application_data',
          'unknown'
        ] as const
      },
      vendor: {
        type: 'string',
        enum: [
          'Yaskawa',
          'FANUC',
          'ABB',
          'KUKA',
          'Kawasaki',
          'Mitsubishi',
          'Nachi',
          'Denso',
          'Staubli',
          'UniversalRobots',
          'Comau',
          'Yamaha',
          'Other'
        ] as const
      },
      vision_system: {
        type: 'string'
      },
      type_label: {
        type: 'string'
      },
      operations: {
        type: 'array',
        items: {
          type: 'string'
        }
      },
      has_vision_data_file: {
        type: 'boolean'
      },
      vision_data_size_bytes: {
        type: 'number'
      },
      source_file: {
        type: 'string'
      },
      source_format: {
        type: 'string'
      },
      comment: {
        type: 'string'
      },
      needs_review: {
        type: 'boolean'
      }
    },
    required: ['name']
  },
  Role: {
    properties: {
      name: {
        type: 'string'
      },
      role_kind: {
        type: 'string',
        enum: [
          'operator',
          'mechanic',
          'electrician',
          'supervisor',
          'engineer',
          'qa',
          'sanitation',
          'other'
        ] as const
      },
      description: {
        type: 'string'
      }
    },
    required: ['name']
  },
  ProtectiveFunction: {
    properties: {
      name: {
        type: 'string'
      },
      function_type: {
        type: 'string',
        enum: [
          'sif',
          'ipf',
          'alarm_ipl',
          'fire_gas',
          'bpcs_ipl',
          'prd',
          'nrv',
          'ros',
          'other'
        ] as const
      },
      description: {
        type: 'string'
      },
      ipl_description: {
        type: 'string'
      },
      safe_state: {
        type: 'string'
      },
      demand_mode: {
        type: 'string'
      },
      sil: {
        type: 'string'
      },
      required_sil: {
        type: 'string'
      },
      required_rrf: {
        type: 'string'
      },
      sil_pfd_avg: {
        type: 'string'
      },
      sil_verification_snapshot: {
        type: 'string'
      },
      sil_verification_source: {
        type: 'string'
      },
      pfd_avg: {
        type: 'string'
      },
      response_time: {
        type: 'string'
      },
      process_safety_time: {
        type: 'string'
      },
      bypass: {
        type: 'string'
      },
      reset: {
        type: 'string'
      },
      alarm: {
        type: 'string'
      },
      logic_solver: {
        type: 'string'
      },
      system_id: {
        type: 'string'
      },
      scenario: {
        type: 'string'
      },
      purpose: {
        type: 'string'
      },
      service: {
        type: 'string'
      },
      ipl_pha_type: {
        type: 'string'
      },
      ipl_credit: {
        type: 'string'
      },
      ipl_category: {
        type: 'string'
      },
      ipl_per_14c: {
        type: 'boolean'
      },
      hazop_system: {
        type: 'string'
      },
      admin_control: {
        type: 'string'
      },
      auditing_method: {
        type: 'string'
      },
      auditing_frequency: {
        type: 'string'
      },
      ipl_other: {
        type: 'string'
      },
      energy_supply: {
        type: 'string'
      },
      special_requirements: {
        type: 'string'
      },
      manual_shutdown: {
        type: 'string'
      },
      hmi: {
        type: 'string'
      },
      human_response: {
        type: 'string'
      },
      startup_mode: {
        type: 'string'
      },
      normal_mode: {
        type: 'string'
      },
      maintenance_mode: {
        type: 'string'
      },
      emergency_shutdown_mode: {
        type: 'string'
      },
      protection_method: {
        type: 'string'
      },
      degradation: {
        type: 'string'
      },
      full_integrated_testing: {
        type: 'string'
      },
      sensor_fe_interfaces: {
        type: 'string'
      },
      sensor_group_voting: {
        type: 'string'
      },
      fe_group_voting: {
        type: 'string'
      },
      signal_filtering: {
        type: 'string'
      },
      fe_bypassing: {
        type: 'string'
      },
      air_supply: {
        type: 'string'
      },
      air_supply_test_interval: {
        type: 'string'
      },
      air_supply_test_effectiveness: {
        type: 'string'
      },
      end_of_useful_life: {
        type: 'string'
      },
      common_cause_failure: {
        type: 'string'
      },
      shutdown_sequence: {
        type: 'string'
      },
      secondary_hazard: {
        type: 'string'
      },
      consequences: {
        type: 'string'
      },
      associated_ipls: {
        type: 'string'
      },
      corrective_actions: {
        type: 'string'
      },
      rrf: {
        type: 'string'
      },
      mttfs: {
        type: 'string'
      },
      mission_time_years: {
        type: 'string'
      },
      startup_time_hours: {
        type: 'string'
      },
      str_specified: {
        type: 'string'
      },
      sil_ac: {
        type: 'string'
      },
      sil_capability: {
        type: 'string'
      },
      pfd_contrib_sensor: {
        type: 'string'
      },
      pfd_contrib_ls: {
        type: 'string'
      },
      pfd_contrib_fe: {
        type: 'string'
      },
      mci_sensor: {
        type: 'string'
      },
      mci_ls: {
        type: 'string'
      },
      mci_fe: {
        type: 'string'
      },
      mttfs_sensor: {
        type: 'string'
      },
      mttfs_ls: {
        type: 'string'
      },
      mttfs_fe: {
        type: 'string'
      },
      sil_ac_sensor: {
        type: 'string'
      },
      sil_ac_ls: {
        type: 'string'
      },
      sil_ac_fe: {
        type: 'string'
      },
      sil_capability_sensor: {
        type: 'string'
      },
      sil_capability_ls: {
        type: 'string'
      },
      sil_capability_fe: {
        type: 'string'
      },
      sensor_count: {
        type: 'string'
      },
      final_element_count: {
        type: 'string'
      },
      docs_path: {
        type: 'string'
      }
    },
    required: ['name']
  },
  LogicSolver: {
    properties: {
      name: {
        type: 'string'
      },
      type: {
        type: 'string'
      },
      vendor: {
        type: 'string'
      },
      application: {
        type: 'string'
      },
      power_loss_action: {
        type: 'string'
      },
      safe_state: {
        type: 'string'
      },
      fault_diagnostics: {
        type: 'string'
      },
      proof_test_coverage: {
        type: 'string'
      },
      mttr_hours: {
        type: 'string'
      },
      test_interval: {
        type: 'string'
      },
      voting: {
        type: 'string'
      },
      degradation: {
        type: 'string'
      }
    },
    required: ['name']
  },
  SrsSection: {
    properties: {
      name: {
        type: 'string'
      },
      header: {
        type: 'string'
      },
      function_group: {
        type: 'string'
      },
      function_category: {
        type: 'string'
      },
      package_id: {
        type: 'string'
      },
      package_title: {
        type: 'string'
      },
      docs_path: {
        type: 'string'
      },
      docs_anchor: {
        type: 'string'
      }
    },
    required: ['name']
  },
  ReferenceDocument: {
    properties: {
      name: {
        type: 'string'
      },
      document_number: {
        type: 'string'
      },
      document_type: {
        type: 'string'
      },
      description: {
        type: 'string'
      },
      revision: {
        type: 'string'
      },
      package_id: {
        type: 'string'
      }
    },
    required: ['name']
  },
  HazardScenario: {
    properties: {
      name: {
        type: 'string'
      },
      consequences: {
        type: 'string'
      },
      associated_ipls: {
        type: 'string'
      },
      corrective_actions: {
        type: 'string'
      }
    },
    required: ['name']
  },
  FunctionLogicDiagram: {
    properties: {
      name: {
        type: 'string'
      },
      fld_number: {
        type: 'string'
      },
      description: {
        type: 'string'
      },
      application: {
        type: 'string'
      }
    },
    required: ['name']
  },
  SafetyPoint: {
    properties: {
      name: {
        type: 'string'
      },
      point_type: {
        type: 'string'
      },
      description: {
        type: 'string'
      },
      safety_related: {
        type: 'boolean'
      },
      tag_number: {
        type: 'string'
      },
      location: {
        type: 'string'
      },
      unit: {
        type: 'string'
      },
      subunit: {
        type: 'string'
      },
      sil: {
        type: 'string'
      },
      fld_number: {
        type: 'string'
      },
      application_address: {
        type: 'string'
      },
      block_name: {
        type: 'string'
      },
      chassis_id: {
        type: 'string'
      },
      slot_number: {
        type: 'string'
      },
      channel_number: {
        type: 'string'
      },
      data_type: {
        type: 'string'
      },
      signal_type: {
        type: 'string'
      },
      engineering_units: {
        type: 'string'
      },
      soe_enable: {
        type: 'boolean'
      },
      force_enable: {
        type: 'boolean'
      },
      write_enable: {
        type: 'boolean'
      },
      is_internal: {
        type: 'boolean'
      }
    },
    required: ['name']
  },
  SoapEndpoint: {
    properties: {
      name: {
        type: 'string'
      },
      url: {
        type: 'string'
      },
      auth_kind: {
        type: 'string',
        enum: ['none', 'basic', 'ntlm'] as const
      },
      enabled: {
        type: 'boolean'
      },
      has_credentials: {
        type: 'boolean'
      },
      current_contract_hash: {
        type: 'string'
      },
      description: {
        type: 'string'
      }
    },
    required: ['name', 'url', 'current_contract_hash']
  },
  SoapContract: {
    properties: {
      name: {
        type: 'string'
      },
      contract_hash: {
        type: 'string'
      },
      target_namespace: {
        type: 'string'
      },
      wsdl_url: {
        type: 'string'
      },
      service_name: {
        type: 'string'
      },
      port_name: {
        type: 'string'
      },
      binding_name: {
        type: 'string'
      },
      soap_version: {
        type: 'string',
        enum: ['1.1', '1.2'] as const
      },
      discovered_at: {
        type: 'string'
      },
      description: {
        type: 'string'
      }
    },
    required: ['name', 'contract_hash']
  },
  SoapOperation: {
    properties: {
      name: {
        type: 'string'
      },
      operation_key: {
        type: 'string'
      },
      soap_action: {
        type: 'string'
      },
      input_message: {
        type: 'string'
      },
      output_message: {
        type: 'string'
      },
      input_element: {
        type: 'string'
      },
      output_element: {
        type: 'string'
      },
      input_schema_json: {
        type: 'string'
      },
      output_schema_json: {
        type: 'string'
      },
      access: {
        type: 'string',
        enum: ['disabled', 'read', 'write'] as const
      },
      agent_enabled: {
        type: 'boolean'
      },
      response_encoding: {
        type: 'string',
        enum: ['soap_body', 'result_text', 'embedded_xml', 'embedded_json'] as const
      },
      description: {
        type: 'string'
      }
    },
    required: ['name', 'operation_key']
  },
  Area: {
    properties: {
      name: {
        type: 'string'
      },
      area_kind: {
        type: 'string',
        enum: [
          'production_zone',
          'cooler',
          'freezer',
          'utility_room',
          'machinery_room',
          'mezzanine',
          'outdoor',
          'other'
        ] as const
      },
      temperature_class: {
        type: 'string',
        enum: ['ambient', 'refrigerated', 'frozen', 'heated'] as const
      },
      description: {
        type: 'string'
      }
    },
    required: ['name']
  },
  FaultSymptom: {
    properties: {
      symptom: {
        type: 'string'
      },
      kind: {
        type: 'string',
        enum: ['fault', 'obstruction', 'housekeeping', 'other'] as const
      },
      perceivable_by: {
        type: 'array',
        items: {
          type: 'string',
          enum: ['visual', 'thermal', 'acoustic', 'tactile'] as const
        }
      },
      severity: {
        type: 'string'
      },
      description: {
        type: 'string'
      }
    },
    required: ['symptom']
  },
  Observer: {
    properties: {
      kind: {
        type: 'string',
        enum: ['reviewer', 'operators', 'other'] as const
      },
      name: {
        type: 'string'
      },
      status: {
        type: 'string'
      }
    },
    required: ['kind', 'name']
  },
  FaultCause: {
    properties: {
      cause: {
        type: 'string'
      },
      probability: {
        type: 'number'
      },
      description: {
        type: 'string'
      }
    },
    required: ['cause']
  },
  Problem: {
    properties: {
      record_kind: {
        type: 'string',
        enum: ['concept'] as const
      },
      name: {
        type: 'string'
      },
      description: {
        type: 'string'
      },
      status: {
        type: 'string',
        enum: ['open', 'diagnosing', 'diagnosed', 'cleared'] as const
      },
      opened_at: {
        type: 'string'
      },
      cleared_at: {
        type: 'string'
      },
      acknowledged_at: {
        type: 'string'
      },
      acknowledged_by: {
        type: 'string'
      },
      source: {
        type: 'string',
        enum: ['custom_agent', 'notification_rule'] as const
      },
      owner_id: {
        type: 'string'
      },
      trigger_client_id: {
        type: 'string'
      },
      investigated_by_run_id: {
        type: 'string'
      },
      resolution_status: {
        type: 'string',
        enum: ['open', 'closed'] as const
      },
      closed_at: {
        type: 'string'
      },
      closed_by: {
        type: 'string'
      }
    },
    required: [],
    oneOf: [
      {
        properties: {
          status: false,
          opened_at: false,
          cleared_at: false,
          acknowledged_at: false,
          acknowledged_by: false,
          source: false,
          owner_id: false,
          trigger_client_id: false,
          investigated_by_run_id: false,
          resolution_status: false,
          closed_at: false,
          closed_by: false
        },
        required: ['record_kind', 'name', 'description']
      },
      {
        properties: { record_kind: false },
        required: ['status', 'opened_at', 'source', 'owner_id', 'trigger_client_id']
      }
    ]
  },
  Intent: {
    properties: {
      name: {
        type: 'string'
      },
      description: {
        type: 'string'
      },
      examples: {
        type: 'array',
        items: {
          type: 'string'
        }
      }
    },
    required: ['name']
  },
  OperatorPhrase: {
    properties: {
      phrase: {
        type: 'string'
      },
      intent_name: {
        type: 'string'
      },
      description: {
        type: 'string'
      }
    },
    required: ['phrase']
  },
  Utility: {
    properties: {
      name: {
        type: 'string'
      },
      utility_kind: {
        type: 'string',
        enum: [
          'electrical',
          'steam',
          'refrigeration',
          'water',
          'compressed_air',
          'gas',
          'drainage',
          'ventilation',
          'instrument_air',
          'nitrogen',
          'co2',
          'vacuum',
          'hydraulic',
          'fuel_oil',
          'condensate',
          'glycol',
          'other'
        ] as const
      },
      utility_kind_other: {
        type: 'string'
      },
      voltage_class: {
        type: 'string'
      },
      water_kind: {
        type: 'string'
      },
      refrigerant_type: {
        type: 'string'
      },
      description: {
        type: 'string'
      }
    },
    required: ['name', 'utility_kind']
  },
  VisionSystem: {
    properties: {
      name: {
        type: 'string'
      },
      vendor: {
        type: 'string',
        enum: ['Cognex', 'Other'] as const
      },
      product_family: {
        type: 'string',
        enum: ['In-Sight', 'DataMan', 'VisionPro', 'Unknown'] as const
      },
      model: {
        type: 'string'
      },
      host_name: {
        type: 'string'
      },
      ip_address: {
        type: 'string'
      },
      mac_address: {
        type: 'string'
      },
      serial_number: {
        type: 'string'
      },
      firmware_version: {
        type: 'string'
      },
      plc_module_name: {
        type: 'string'
      },
      plc_module_catalog_number: {
        type: 'string'
      },
      startup_job: {
        type: 'string'
      },
      job_count: {
        type: 'number'
      },
      image_count: {
        type: 'number'
      },
      source_files: {
        type: 'array',
        items: {
          type: 'string'
        }
      },
      source_file: {
        type: 'string'
      }
    },
    required: ['name']
  },
  VisionJob: {
    properties: {
      name: {
        type: 'string'
      },
      file_name: {
        type: 'string'
      },
      format: {
        type: 'string',
        enum: ['job', 'jobx', 'dataman_cfg', 'dataman_cdc', 'vpp'] as const
      },
      size_bytes: {
        type: 'number'
      },
      content_hash: {
        type: 'string'
      },
      detected_functions: {
        type: 'array',
        items: {
          type: 'string'
        }
      },
      is_startup_job: {
        type: 'boolean'
      },
      job_format_version: {
        type: 'string'
      },
      firmware_version: {
        type: 'string'
      },
      camera_model: {
        type: 'string'
      },
      job_type: {
        type: 'string'
      },
      cell_count: {
        type: 'number'
      },
      named_cell_count: {
        type: 'number'
      },
      io_cells: {
        type: 'array',
        items: {
          type: 'string'
        }
      },
      plc_output_fields: {
        type: 'array',
        items: {
          type: 'string'
        }
      },
      plc_input_fields: {
        type: 'array',
        items: {
          type: 'string'
        }
      },
      opaque_sheets: {
        type: 'array',
        items: {
          type: 'string'
        }
      },
      source_code: {
        type: 'string'
      },
      source_code_truncated: {
        type: 'boolean'
      },
      source_file: {
        type: 'string'
      }
    },
    required: ['name']
  }
}
